import { isNilOrEmpty, dotPathEq, isArray, dispatch, dotPath, dotPathOr, toUpperFirst, dotPathSatisfies, isNotNil, dotPathNotEq, isString, isTruthy } from 'ramda-extension';
import { compose, join, values, map, ifElse, head, identity, either, reject, isEmpty, defaultTo, always, find, isNil, allPass, complement, reduce, keys, of, prop, converge, concat, toLower, replace, equals, filter, cond, pick, T, flatten, chain, omit, test, has, both, take, propEq, propSatisfies, uncurryN, any } from 'ramda';

const GENERIC_ERROR = 'Unable to complete your request.';
const GENERIC_NETWORK_ERROR = `${GENERIC_ERROR} (Network error)`;
const GENERIC_SERVER_ERROR = `${GENERIC_ERROR} (Server error)`;
const TRY_AGAIN_MESSAGE =
  'Please try again later, or contact customer support.';

const getFieldErrorValues = compose(join(', '), values);

const DEFAULT_ERROR_MESSAGE = 'Unable to submit the form.';

/**
 * There are times where showing field errors along their respective fields is not
 * possible or good UX. One alternative is to show all error text together, in a
 * separate place.  This function combines all field errors into a comma delimited
 * list, along with a message one line above this.
 *
 * @param {object} parsedErrorResponse - A previously parsed GraphQL error response.
 * @param {string} [fallbackTopErrorMessage=DEFAULT_ERROR_MESSAGE] - What to show in the line above the
 * combined list of field errors.
 * @returns {string} The combined error message.
 */
const combineTopAndFieldErrors = (
  parsedErrorResponse,
  fallbackTopErrorMessage = DEFAULT_ERROR_MESSAGE
) => {
  const formError = isNilOrEmpty(parsedErrorResponse.humanReadableErrorMessage)
    ? fallbackTopErrorMessage
    : parsedErrorResponse.humanReadableErrorMessage;

  if (!isNilOrEmpty(parsedErrorResponse.fieldErrors)) {
    return [
      formError,
      getFieldErrorValues(parsedErrorResponse.fieldErrors),
    ].join('\n');
  } else {
    return formError;
  }
};

const isSubmissionError$1 = dotPathEq('name', 'SubmissionError');

/**
 * Change a fieldError array value to a string by plucking the first
 * array entry.  Others are discarded.
 *
 * @param {object} fieldErrors - The fieldError object.
 */
const flattenValues = map(ifElse(isArray, head, identity));

/**
 * Paths in the response where the Error that contains the fieldErrors
 * could exist in. In the wild, OP fieldError responses have been
 * spotted in several locations within the response.  Ruby
 * ones are elsewhere as well.
 *
 * This does not include wb-error-node SubmissionError paths, as that
 * detection is done in findFieldErrors.
 */
const ERROR_PATHS = [
  // OP, variant 1
  'meta.response.data.fieldErrors',
  // OP, variant 2
  'extensions.exception.response.data.fieldErrors',
  // ruby
  'extensions.exception.response.data.error.fieldErrors',
];

const findSubmissionError = (error) =>
  compose(
    either(dotPath('errors'), identity),
    find(compose(isSubmissionError$1)),
    reject(isNil),
    map((obj) => dotPath(obj, error))
  )(['extensions.exception', 'extensions.exception.response.data.error']);

/**
 * Get a fieldErrors or (in the case of wb-error-node using apps)
 * the fieldError equivalent.
 *
 * Node apps that use the wb-error-node NPM create an Error of
 * type SubmissionError. That variant is retrofitted into the
 * fieldError scheme used by Ruby and OP apps. Since this error
 * has a "name" attribute, this looks for that first, and then
 * falls back to searching for ruby/OP variants.
 *
 * @param {object} error - A single error object from the Apollo
 * "errors" response array.
 */
const findFieldErrors = either(
  findSubmissionError,
  dispatch(ERROR_PATHS.map((p) => dotPath(p)))
);

/**
 *
 * Look for fieldError instances or equivalents: these types of errors
 * include an object that contains key value pairs with error messages
 * specific to individual fields. Most likely, these are in response to
 * a form submission where the fields are input fields, and the resulting
 * messages are to be shown along those fields.
 *
 * @param {object} error - The error as thrown by Apollo.
 *
 * @returns {object} An object containing the key/value field/errors.
 *
 */
const getFieldErrors = compose(
  head,
  reject(isEmpty),
  map(compose(flattenValues, defaultTo({}), findFieldErrors)),
  dotPathOr([], 'error.graphQLErrors')
);

/**
 * Edge cases like apisauce.
 */
const parseFieldError = compose(
  ifElse(isEmpty, always(undefined), identity),
  ifElse(
    isSubmissionError$1,
    dotPathOr({}, 'errors'),
    compose(flattenValues, dotPathOr({}, 'fieldErrors'))
  )
);

/**
 * Generates a random int/char string 10 characters long. Typically to be used
 * for reporting errors.
 *
 * @returns {string} A 10 character int/char string.
 *
 * @example
 *
 *   generateErrorCode();
 *
 *   'fd3489fnk9'
 *
 */
const generateErrorCode = () =>
  [...Array(10)].map(() => Math.random().toString(36)[2]).join('');

const isInvalidLoginError = allPass([
  dotPathEq('status', 401),
  dotPathEq('name', 'UnauthorizedError'),
  dotPathEq('message', 'Invalid username or password.'),
]);

const isSubmissionError = allPass([
  dotPathEq('status', 400),
  dotPathEq('name', 'SubmissionError'),
  either(dotPath('error'), dotPath('errors')),
]);

/**
 * A more generic version of isInvalidLoginError.
 */
const isUnauthorizedError = allPass([
  dotPathEq('status', 401),
  dotPathEq('name', 'UnauthorizedError'),
]);

/**
 * If the WB auth service error is not one that is typically expected,
 * such as a invalid username/password, or trying to log in with
 * a Facebook account email, this function returns true.
 *
 * @param {object} error - The individual error object (as opposed to
 *   an entire error response);.
 * @returns {boolean} - If the error is unexpected.
 */
const isUnexpectedEmailLoginError = complement(
  either(isUnauthorizedError, isSubmissionError)
);

const isWbCodedError = allPass([
  dotPathEq('status', 400),
  dotPathEq('name', 'WbCodedError'),
]);

const mapErrors$1 = (messages) => {
  const accumulator = {};
  return reduce(
    (acc, cur) => {
      const id = generateErrorCode();
      acc[id] = cur;
      return acc;
    },
    accumulator,
    messages
  );
};

/*
 * There are a multitude of error scenarios which result in varying payloads.
 * The parse* functions in this NPM attempt to normalize them into a subset
 * of attributes.  Then they should call this function to yield the standard
 * structure returned by this function.
 *
 * @returns {Object} An object containing interesting bits about
 *   the parsed error.
 *   - errorCodes: codes generated by getnerateErrorCode() to identify each error.
 *   - errors: a map of error code and (possibly) non-human-readable error.
 *   - fieldErrors: an object of key/value pairs of field/error messages.
 *   - humanReadableErrorMessage: all humanReadableErrors joined with a comma.
 *   - humanReadableErrors: an array of strings of (hopefully) human readable
 *     error messages.
 *   - statusCode: the HTTP status code of the response (this does not include
 *     HTTP status codes of other API calls to other backends made by the top
 *     level request).
 *   - wbErrorCode: if the error was of type WbCodedError, the code
 *     is returned here.
 */
const parsedErrorResponse = ({
  errors,
  fieldErrors,
  humanReadableErrors,
  networkError,
  statusCode,
  wbErrorCode,
}) => ({
  errorCodes: keys(errors),
  // TODO: errorMessage is deprecated, use humanReadableErrorMessage
  errorMessage: humanReadableErrors.join(', '),
  errors,
  fieldErrors,
  humanReadableErrors,
  humanReadableErrorMessage: humanReadableErrors.join(', '),
  networkError,
  statusCode,
  wbErrorCode,
});

const submissionErrorHumanReadableErrors = ifElse(
  dotPath('error'),
  compose(of, dotPath('error')),
  compose(values, dotPath('errors'))
);

const DATA_ERROR_PATH = 'data.error';

/**
 * Get the HTTP status code.
 *
 * @param Object - Error - the error from the apisauce operation.
 */
const statusCode$1 = dotPathOr(null, 'status');

const wbErrorCode$1 = compose(
  ifElse(isWbCodedError, prop('message'), always(null)),
  dotPath(DATA_ERROR_PATH)
);

/**
 * Return the human readable generic error associated with the apisauce problem code.
 *
 * @param Object - Error - the error from the apisauce operation.
 */
const humanReadableProblemMessage = converge(concat, [
  always(GENERIC_ERROR),
  compose((str) => ` (${str})`, toUpperFirst, toLower, replace('_', ' ')),
]);

const humanReadableProblem = ifElse(
  equals('NONE'),
  always(null),
  humanReadableProblemMessage
);

const onlyHasProblem = allPass([
  dotPathSatisfies(isNotNil, 'problem'),
  dotPathNotEq('problem', 'NONE'),
  dotPathSatisfies(isNil, DATA_ERROR_PATH),
]);

const findErrors$1 = compose(
  filter(identity),
  of,
  cond([
    [onlyHasProblem, pick(['problem'])],
    [dotPath(DATA_ERROR_PATH), dotPath(DATA_ERROR_PATH)],
    [T, identity],
  ])
);

const isError500$1 = dotPathEq('status', 500);

/**
 * Generate errors that can be displayed in the UI.
 *
 */
const generateHumanReadableErrors$2 = compose(
  filter(identity),
  flatten,
  map(
    cond([
      [dotPath('problem'), compose(humanReadableProblem, prop('problem'))],
      [isError500$1, always(GENERIC_SERVER_ERROR)],
      [isSubmissionError, submissionErrorHumanReadableErrors],
      [T, dotPath('message')],
    ])
  ),
  values
);

const parseApisauceErrorResponse = (response) => {
  const errors = compose(mapErrors$1, findErrors$1)(response);

  return parsedErrorResponse({
    errors,
    fieldErrors: parseFieldError(dotPath(DATA_ERROR_PATH)),
    humanReadableErrors: generateHumanReadableErrors$2(errors),
    networkError: response.problem === 'NONE' ? null : response.problem,
    statusCode: statusCode$1(response),
    wbErrorCode: wbErrorCode$1(response),
  });
};

const AXIOS_ERROR_RESPONSE_PATH = 'extensions.exception.response.data';
const AXIOS_ERROR_RESPONSE_PATH_FALLBACK = 'meta.response.data';
const HUMAN_READABLE_BLACKLIST = /Service Error/i;

/**
 * The GraphQL server used to put errors in 'meta', via its 'formatError' function.  At some point,
 * the response stopped including that field, possibly <a href="https://github.com/graphql/graphql-js/commit/0bb47b229a369370e5698d820dd8626c6546df49#diff-f8e4bedf89ec2148ccb1602bfc6a7ae7">in this commit</a>.
 *
 * The 'formatError' function now requires a strict format, where custom fields can only exist under the "extensions" object.
 * Luckily, our implementation of 'formatError' inserts the axios meta inside of "extensions.exception", in addition to "meta".
 *
 * This function will look in the "extensions.exception" path for the Axios meta, and fall back to the old "meta" key.
 *
 * @param {Function} testFn - The test function to pass to dotPathSatisfies.
 * @param {string} path
 * @returns {string}
 */
const axiosPathSatisfies = (testFn, path) =>
  either(
    dotPathSatisfies(testFn, `${AXIOS_ERROR_RESPONSE_PATH}.${path}`),
    dotPathSatisfies(testFn, `${AXIOS_ERROR_RESPONSE_PATH_FALLBACK}.${path}`)
  );

/**
 * Look for the given path inside the axios response, falling back to the 'meta' key. See the "axiosPathSatisfies" docs for more info.
 *
 * @param {string} path
 * @returns {string}
 */
const axiosPath = (path) =>
  either(
    dotPath(`${AXIOS_ERROR_RESPONSE_PATH}.${path}`),
    dotPath(`${AXIOS_ERROR_RESPONSE_PATH_FALLBACK}.${path}`)
  );

const extractGenericErrors = compose(
  chain(values),
  values,
  omit(['fieldErrors']),
  either(
    dotPath(`${AXIOS_ERROR_RESPONSE_PATH}`),
    dotPathOr({}, `${AXIOS_ERROR_RESPONSE_PATH_FALLBACK}`)
  )
);

const hasBackendAxiosErrorMessage = axiosPathSatisfies(
  isString,
  'error.message'
);

const hasBackendAxiosError = axiosPathSatisfies(
  either(isString, isSubmissionError),
  'error'
);

const parseErrorObject = ifElse(
  hasBackendAxiosErrorMessage,
  axiosPath('error.message'),
  extractGenericErrors
);

const networkError = compose(dotPathOr('', 'networkError'));

const getErrorMessage = ifElse(
  hasBackendAxiosError,
  axiosPath('error'),
  parseErrorObject
);

const filterBlackList = ifElse(
  test(HUMAN_READABLE_BLACKLIST),
  always(GENERIC_ERROR),
  identity
);

const generateHumanReadableErrors$1 = compose(
  map(filterBlackList),
  chain(identity),
  reject(compose(either(has('fieldErrors'), isSubmissionError), defaultTo({}))),
  map(getErrorMessage),
  dotPathOr([], 'graphQLErrors')
);

// OP puts the id at the top level, instead of inside the error object
const hasAxiosOpErrorId = both(
  dotPathSatisfies(isTruthy, `${AXIOS_ERROR_RESPONSE_PATH}.error`),
  dotPathSatisfies(isTruthy, `${AXIOS_ERROR_RESPONSE_PATH}.id`)
);

const getErrorId = compose(
  take(10),
  cond([
    [axiosPathSatisfies(isTruthy, 'error.id'), axiosPath('error.id')],
    [hasAxiosOpErrorId, dotPath(`${AXIOS_ERROR_RESPONSE_PATH}.id`)],
    [T, generateErrorCode],
  ])
);

const mapErrors = (error, accumulator) =>
  compose(
    reduce((acc, cur) => {
      const id = getErrorId(cur);
      acc[id] = { ...cur, message: getErrorMessage(cur) };
      return acc;
    }, accumulator),
    dotPathOr([], 'graphQLErrors')
  )(error);

/**
 * Extract several useful items from a graphql data object that contains errors.
 * This will extract human readable errors from the object and present them
 * as an array, as well as any network error message, error codes, and error
 * codes mapped to the original errors.
 *
 * This will allow us to present error codes to the user which we can then
 * reference in Rollbar to track down individual errors and solve user issues
 * in a much better way.
 *
 * @param {object} data - The data object from an Apollo request.
 * @returns {object} An object containing useful error information.
 *
 * @example
 *
 *   parseGraphqlErrorResponse(data);
 *
 *   {
 *     networkError: 'Some network error message',
 *     errorCodes: ['123', 'abc'],
 *     errors: {
 *       '123': { ...errorObject },
 *       'abc': { ...anotherErrorObject },
 *     },
 *     humanReadableErrors: ['This must be a string', 'Or a number']
 *   }
 *
 */
const parseGraphqlErrorResponse = (data) => {
  const error = dotPathOr({}, 'error', data);
  const errors = mapErrors(error, {});
  const errorCodes = keys(errors);
  const humanReadableErrors = generateHumanReadableErrors$1(error);

  return {
    errorCodes,
    // TODO: errorMessage is deprecated, use humanReadableErrorMessage
    errorMessage: humanReadableErrors.join(', '),
    errors,
    fieldErrors: getFieldErrors(data),
    humanReadableErrors,
    humanReadableErrorMessage: humanReadableErrors.join(', '),
    networkError: networkError(error),
  };
};

/**
 * Parse a response received as `result.errors` from a GraphQL mutation. This
 * simply puts the errors into the structure expected from the sister function
 * `parseGraphqlErrorResponse` which does all the heavy lifting.
 *
 * @param {object} response - A GraphQL mutation response that contains an array
 *   of errors.
 * @returns {object} An object containing useful error information.
 *
 */
const parseGraphqlMutationErrors = (response) => {
  const data = {
    error: {
      graphQLErrors: response.errors,
    },
  };

  return parseGraphqlErrorResponse(data);
};

/**
 * This is the error caused by the inability of the Apollo client to
 * connect to our backend. For these, there is no
 * meta.request/meta.response, because the Apollo request never made it
 * to GraphQL to begin with, and so no additional call to a backend
 * service was made.  Instead, the error is an object in the networkError
 * attribute.
 */
const isGraphqlNetworkRequestFailedError = allPass([
  propEq('message', 'Network error: Network request failed'),
  propSatisfies(isEmpty, 'graphQLErrors'),
  propSatisfies(complement(isEmpty), 'networkError'),
]);

const toGenericNetworkRequestFailedError = (err) => ({
  ...err.networkError,
  message: GENERIC_NETWORK_ERROR,
});

const RESULT_ERROR_PATH = 'networkError.result.error';
const RESULT_ERRORS_PATH = 'networkError.result.errors';

const wbErrorCode = compose(
  ifElse(isWbCodedError, prop('message'), always(null)),
  dotPath(RESULT_ERROR_PATH)
);

const parseStatusCodeFromMessage = (message) => {
  const re = new RegExp('Response not successful: Received status code (\\d+)');
  const match = re.exec(message);
  if (match) {
    return parseInt(match[1], 10);
  } else {
    return null;
  }
};

const resultErrors = dotPathOr([], RESULT_ERRORS_PATH);

/**
 * Get the HTTP status code of the GraphQL request itself, from either
 * the result error status path, or error message itself.
 *
 * (This does not include any error HTTP status code from a
 * backend API HTTP error, which would show up in the graphQLErrors array.).
 *
 * @param Object - Error - the error from the GraphQL operation.
 */

const statusCodeFromResult = dotPath(`${RESULT_ERROR_PATH}.status`);

const statusCodeFromMessage = compose(
  parseStatusCodeFromMessage,
  dotPathOr('', 'message')
);

const statusCode = either(statusCodeFromResult, statusCodeFromMessage);

/**
 * Find non-human readable error messages.  These can come from:
 *  1. The "error.result.errors" array, or
 *  2. When there is a 400 HTTP response code (maybe others?), the
 *     "error.network.result" object.
 *  2b. When there is a 500 HTTP response code, the
 *     "error.network.message" string.
 *  2c. When the client fails to make a connection, the
 *     "error.message" string.
 *
 * @param Object - Error - the error from the GraphQL operation.
 */
const findErrors = compose(
  filter(identity),
  cond([
    [compose(complement(isEmpty), resultErrors), resultErrors],
    [
      isGraphqlNetworkRequestFailedError,
      compose(of, toGenericNetworkRequestFailedError),
    ],
    [
      T,
      compose(
        of,
        dispatch([
          dotPath(RESULT_ERROR_PATH),
          dotPath('networkError.error'),
          dotPath('networkError'),
          identity,
        ])
      ),
    ],
  ])
);

const isError500 = either(
  compose(equals(500), statusCodeFromMessage),
  compose(equals(500), dotPath('status'))
);

/**
 * Generate errors that can be displayed in the UI.
 *
 * If the Apollo client was unable to reach the GraphQL server,
 * return a canned error: <pre>GENERIC_NETWORK_ERROR</pre>.
 *
 * If the GraphQL request itself yielded a non-successful HTTP
 * response, return a different canned error: <pre>GENERIC_SERVER_ERROR</pre>.
 *
 * Otherwise, try and fetch the message from the "error" or
 * "error.message" properties.
 */
const generateHumanReadableErrors = compose(
  filter(identity),
  flatten,
  map(
    cond([
      [isError500, always(GENERIC_SERVER_ERROR)],
      [isSubmissionError, submissionErrorHumanReadableErrors],
      [T, compose(ifElse(isString, identity, dotPath('message')))],
    ])
  ),
  values
);

/**
 * When a GraphQL mutation or query experiences an error within its
 * own HTTP request (rather than an error with a backend API call
 * made by the GraphQL server), the Apollo client throws an error,
 * and inside the error object, the details are usually in the
 * "networkError" attribute.
 *
 * @param {object} error - The error as thrown by Apollo.

 * @returns {object} An object containing interesting bits about
 *   the parsed error.
 *   - errorCodes: codes (generated by this file) to identify each
 *     error.
 *   - errors: a map of error code and (possibly) non-human-readable error.
 *   - humanReadableErrors: an array of strings of (hopefully) human readable
 *     error messages.
 *   - errorMessage: all humanReadableErrors joined with a comma.
 *   - wbErrorCode: if the error was of type WbCodedError, the code
 *     is returned here.
 *   - statusCode: the HTTP status code of the Apollo request's response.
 *
 */
const parseNetworkErrorResponse = (error) => {
  const errors = compose(mapErrors$1, findErrors)(error);
  return parsedErrorResponse({
    errors,
    // seems unlikely to get fieldErrors in a network error response,
    // but maybe there are use cases of GraphQL-wrapped REST APIs.
    fieldErrors: getFieldErrors(error),
    humanReadableErrors: generateHumanReadableErrors(errors),
    networkError: error.networkError,
    statusCode: statusCode(error),
    wbErrorCode: wbErrorCode(error),
  });
};

/**
 * @typedef {Function} ReauthorizeFunction
 *
 *  Function to reauthorize the user. Typically for refreshing a JWT.
 *
 * @param {object} [operation] - The GraphQL operation that yielded the unauthorized response.
 * @param {(operation: any) => Promise<boolean>} operation.reauthorize
 * @param {object} operation.excludeOperation
 * @param {*} operation.log
 * @returns {boolean} - Return true if the authorization was successful, false if not.
 * If true, it will trigger the RetryLink to try again, under the assumption the user
 * is now authorized.
 */

/**
 * Trigger a retry if the request returned a HTTP 401.
 *
 * The retryIf function {@link https://github.com/apollographql/apollo-link/pull/436|supports promises}.
 *
 * @param {object} opts
 * @param {object} [opts.excludeOperation=Ping] - Do not retry for this operation
 * name. The Ping operation should not retry, since it is the one responsible for resolving reauthorization.
 * @param {ReauthorizeFunction} opts.reauthorize - Function to reauthorize the user.
 * @param {*} opts.log
 * @returns {boolean}
 */
const retryIfWithReauthorize =
  ({ reauthorize, excludeOperation = 'Ping', log }) =>
  async (error, operation) => {
    // this should never happen, but this is graphql after all...
    if (!error) return false; // do not retry if no error

    const isUnauthorized = !!error.response && 401 === error.response.status;
    if (operation.operationName === excludeOperation && isUnauthorized) {
      return false;
    }
    if (isUnauthorized) {
      const isSuccess = await reauthorize(operation);
      log &&
        log(
          `Apollo retry link: attempt to reauthorize due to unauthorized response. Success: ${isSuccess}`
        );
      return isSuccess;
    }
    const isClientError =
      error.response?.status >= 400 && error.response?.status <= 499;
    return !isClientError;
  };

/**
 * Convenience method for creating a full parsed error message from a
 * human readable string.
 *
 * @param {string} message - Human readable string (or not human
 *   readable if you fancy it!).
 * @param {string} [wbErrorCode] - WBCodedError code.
 * @returns {ReturnType<parsedErrorResponse>}
 */
const simpleParsedErrorResponse = (message, wbErrorCode = null) => {
  return parsedErrorResponse({
    errors: mapErrors$1([{ message }]),
    humanReadableErrors: [message],
    networkError: null,
    statusCode: null,
    wbErrorCode,
  });
};

const is401Error = uncurryN(2, (error, path) => {
  return dotPathEq(path, 401, error);
});

const anyErrorWith401 = uncurryN(2, (paths, error) => {
  return any(is401Error(error), paths);
});

const anyErrorWithAnyPathWith401 = (paths, response) => {
  const errors = dotPathOr([], 'errors', response);
  return any(anyErrorWith401(paths), errors);
};

/**
 * Checks the GraphQL 'errors' for a 401. This is possible
 * due to the error reporting convention used on our
 * backends, which insert the response HTTP status in the
 * 'extensions.exception' attribute of the response JSON.
 * Older implementations put this status in different paths,
 * thus this function checks them all.
 *
 * @param {object} response - The GraphQL response.
 * @returns {object}
 */
const hasUnauthorizedErrorInQueryResponse = (response) => {
  return anyErrorWithAnyPathWith401(
    [
      'meta.status',
      'meta.response.status',
      'extensions.exception.response.status',
    ],
    response
  );
};

/**
 * When a backend responds with a HTTP 401, the Apollo link chain
 * should know about it, so it can reauthorize and try again.
 *
 * Because the retry-link checks only the top level response status,
 * these underlying 401s would be missed, as the link response would
 * only see the top level HTTP 200 from the GraphQL reuest itself.
 *
 * This ApolloLink handler finds these inner 401 errors and translates
 * them to make them seem like top level 401 errors, so that the retry link
 * will detect them and trigger a retry operation.
 *
 * @param {object} operation
 * @param {Function} forward
 * @returns {object}
 */
const surfaceUnauthorizedErrors = (operation, forward) => {
  return forward(operation).map((data) => {
    if (hasUnauthorizedErrorInQueryResponse(data)) {
      const error = new Error('unauthorized to run this query');
      // duck-type it to match top-level errors
      error.response = { status: 401 }; // wb app
      error.statusCode = 401; // fallback
      throw error;
    }
    return data;
  });
};

export { GENERIC_ERROR, GENERIC_NETWORK_ERROR, GENERIC_SERVER_ERROR, TRY_AGAIN_MESSAGE, combineTopAndFieldErrors, generateErrorCode, getFieldErrorValues, getFieldErrors, isInvalidLoginError, isSubmissionError, isUnauthorizedError, isUnexpectedEmailLoginError, isWbCodedError, parseApisauceErrorResponse, parseGraphqlErrorResponse, parseGraphqlMutationErrors, parseNetworkErrorResponse, retryIfWithReauthorize, simpleParsedErrorResponse, surfaceUnauthorizedErrors };
